import { blobToImage, scaleAndRotate, copyExif } from '../../exif-utils'
import uuidv4 from 'uuid/v4'
import { logException } from '../../logger'

const sourceImagerGeojsonFilename = 'source-imagery.geojson'
const sourceImagerGeojsonPath = `results/${sourceImagerGeojsonFilename}`

export async function createProject({
  apiClient,
  farmId,
  fieldName,
  crop,
  metaData,
  cameraProperties,
  options: uploadOptions,
  projectType,
  processingOptions,
  pipelineProfiles,
}) {
  const features = metaData.features
  const projectOptions = {
    project_type: getBackendProjectType(projectType),
    bands: cameraProperties.bands,
    ...getProjectOptions(uploadOptions, features),
  }

  const { id } = await apiClient.createProject(
    farmId,
    fieldName,
    projectOptions,
    {
      crop,
      relative_altitude: median(
        features.map(f => f.properties.relative_altitude)
      ),
      absolute_altitude: median(
        features.map(f => f.properties.absolute_altitude)
      ),
      camera: getCameraName(features),
      camera_properties: cameraProperties,
      number_cameras: features.length,
      processing_options: {
        ...processingOptions,
        ...getProcessingOptions(uploadOptions),
        ...pipelineProfiles?.[getBackendProjectType(projectType)],
      },
      resources: [
        {
          id: uuidv4(),
          type: 'source_imagery',
          path: sourceImagerGeojsonPath,
        },
      ],
      created_using: 'web-client',
    },
    metaData
  )

  return id
}

export async function updateProject({
  projectId,
  apiClient,
  farmId,
  fieldName,
  crop,
  metaData,
  cameraProperties,
  options: uploadOptions,
  projectType,
  processingOptions,
  pipelineProfiles,
}) {
  const features = metaData.features
  const projectOptions = getProjectOptions(uploadOptions, features)
  await apiClient.updateProject(projectId, {
    ...projectOptions,
    farm_id: farmId,
    fields: fieldName,
    metadata: {
      resources: [
        {
          id: uuidv4(),
          type: 'source_imagery',
          path: sourceImagerGeojsonPath,
        },
      ],
      crop,
      relative_altitude: median(
        features.map(f => f.properties.relative_altitude)
      ),
      absolute_altitude: median(
        features.map(f => f.properties.absolute_altitude)
      ),
      camera: getCameraName(features),
      camera_properties: cameraProperties,
      number_cameras: features.length,
      processing_options: {
        ...processingOptions,
        ...getProcessingOptions(uploadOptions),
        ...pipelineProfiles?.[getBackendProjectType(projectType)],
      },
      project_type: getBackendProjectType(projectType),
      created_using: 'web-client',
    },
  })
}

export async function uploadProject({
  projectId: id,
  apiClient,
  files,
  metaData,
  extraFiles,
  projectType,
  options: uploadOptions,
  progressCallback,
  pipelineProfiles,
  processingOptions,
}) {
  const expressUpload = !!uploadOptions.find(
    o => o.key === 'expressUpload' && o.active
  )
  const fileArray = [
    new File([JSON.stringify(metaData)], sourceImagerGeojsonFilename),
    ...extraFiles,
    ...Object.values(files)
      .filter(f => f.uploadStatus !== 'done')
      .map(f => new File([f.file], f.id, { type: f.file.type })),
  ]
  await apiClient.updateProject(id, { status: 'uploading' })
  if (projectType !== 'geotiff') {
    await apiClient.uploadProjectPhotos(id, fileArray, {
      preproccessCallback,
      progressCallback,
      backOffTime: 15000,
      retries: 7,
    })
  } else {
    const orthoFile = fileArray[fileArray.length - 1]
    await apiClient.uploadProjectPhotos(id, fileArray.slice(0, -1), {
      preproccessCallback,
      progressCallback,
      backOffTime: 15000,
      retries: 7,
    })
    await apiClient.uploadOrtho(id, orthoFile, progressCallback)
  }

  await apiClient.startProcessing(id, {
    ...processingOptions,
    ...pipelineProfiles?.[getBackendProjectType(projectType)],
    ...getProcessingOptions(uploadOptions),
  })

  return id

  function preproccessCallback(f) {
    if (expressUpload && f.type.startsWith('image/')) {
      // Scale down image and make sure original EXIF is copied to
      // the resulting scaled image.
      return blobToImage(f)
        .then(img => {
          const canvas = scaleAndRotate(img, 1, { width: 2000, height: 2000 })
          return new Promise(resolve => {
            canvas.toBlob(blob => {
              return copyExif(f, blob).then(result => {
                result.name = f.name
                resolve(result)
              })
            }, 'image/jpeg')
          })
        })
        .catch(err => {
          logException(err)
          return f
        })
    } else {
      return Promise.resolve(f)
    }
  }
}

function getProjectOptions(uploadOptions, features) {
  return uploadOptions.reduce(
    (os, o) => {
      if (o.active && o.updateProject) {
        o.updateProject(os)
      }

      return os
    },
    {
      survey_date: features[0].properties.datetime,
      photos_count: features.length,
    }
  )
}

function getProcessingOptions(uploadOptions) {
  return uploadOptions.reduce((os, o) => {
    if (o.active && o.updateProcessingOptions) {
      o.updateProcessingOptions(os)
    }
    return os
  }, {})
}

function getBackendProjectType(projectType) {
  // Backend use other type names than the create-upload client does
  switch (projectType) {
    case 'individual':
      return 'individual_images'
    case 'overlapping':
      return 'orthomosaic'
    case 'geotiff':
      return 'geotiff'
    default:
      throw new Error(`Unknown project type "${projectType}.`)
  }
}

export function getFrontendProjectType(projectType) {
  // Reverse of getBackendProjectType: given a backend project type,
  // return the corresponding create-upload project type
  switch (projectType) {
    case 'individual_images':
      return 'individual'
    case 'orthomosaic':
      return 'overlapping'
    case 'geotiff':
      return 'geotiff'
    default:
      throw new Error(`Unknown project type "${projectType}.`)
  }
}

function median(xs) {
  const sorted = xs.filter(x => !!x).sort()

  if (sorted.length < 1) return

  return sorted.length % 2
    ? sorted[Math.floor(sorted.length / 2)]
    : (sorted[sorted.length / 2] + sorted[sorted.length / 2 + 1]) / 2
}

function getCameraName(features) {
  return [features[0].properties.make, features[0].properties.model]
    .filter(s => !!s)
    .join(' ')
}
